import type { ParsedSlide } from "./pptx.ts";

export type SlideStatus = "unchanged" | "changed" | "added" | "removed";

export type DiffRow = {
  key: string;
  status: SlideStatus;
  currentIndex: number | null;
  snapshotIndex: number | null;
  currentText: string | null;
  snapshotText: string | null;
};

export type Segment =
  | { kind: "changes"; rows: DiffRow[] }
  | { kind: "unchanged"; count: number };

const lcsLengths = (currentIds: string[], snapshotIds: string[]): number[][] => {
  const n = currentIds.length;
  const m = snapshotIds.length;
  const dp: number[][] = Array.from({ length: n + 1 }, () =>
    Array.from({ length: m + 1 }, () => 0),
  );
  for (let i = n - 1; i >= 0; i--) {
    const row = dp[i];
    const nextRow = dp[i + 1];
    if (!row || !nextRow) continue;
    for (let j = m - 1; j >= 0; j--) {
      if (currentIds[i] === snapshotIds[j]) {
        row[j] = (nextRow[j + 1] ?? 0) + 1;
      } else {
        row[j] = Math.max(nextRow[j] ?? 0, row[j + 1] ?? 0);
      }
    }
  }
  return dp;
};

const collapseAdjacentReplace = (rows: DiffRow[]): DiffRow[] => {
  const out: DiffRow[] = [];
  for (let i = 0; i < rows.length; i++) {
    const removed = rows[i];
    const added = rows[i + 1];
    if (
      removed?.status === "removed" &&
      added?.status === "added" &&
      removed.snapshotIndex !== null &&
      added.currentIndex !== null
    ) {
      out.push({
        key: `changed-${added.currentIndex.toString()}-${removed.snapshotIndex.toString()}`,
        status: "changed",
        currentIndex: added.currentIndex,
        snapshotIndex: removed.snapshotIndex,
        currentText: added.currentText,
        snapshotText: removed.snapshotText,
      });
      i += 1;
      continue;
    }
    if (removed) out.push(removed);
  }
  return out;
};

export const computeDiff = (
  current: ParsedSlide[],
  snapshot: ParsedSlide[],
): DiffRow[] => {
  const currentIds = current.map((s) => s.slideId);
  const snapshotIds = snapshot.map((s) => s.slideId);
  const dp = lcsLengths(currentIds, snapshotIds);

  const aligned: DiffRow[] = [];
  let i = 0;
  let j = 0;
  while (i < current.length && j < snapshot.length) {
    const c = current[i];
    const s = snapshot[j];
    if (!c || !s) break;

    if (c.slideId === s.slideId) {
      aligned.push({
        key: `pair-${i.toString()}-${j.toString()}`,
        status: c.contentHash === s.contentHash ? "unchanged" : "changed",
        currentIndex: i,
        snapshotIndex: j,
        currentText: c.textContent,
        snapshotText: s.textContent,
      });
      i += 1;
      j += 1;
      continue;
    }

    const down = dp[i + 1]?.[j] ?? 0;
    const right = dp[i]?.[j + 1] ?? 0;
    if (down >= right) {
      aligned.push({
        key: `added-${i.toString()}`,
        status: "added",
        currentIndex: i,
        snapshotIndex: null,
        currentText: c.textContent,
        snapshotText: null,
      });
      i += 1;
    } else {
      aligned.push({
        key: `removed-${j.toString()}`,
        status: "removed",
        currentIndex: null,
        snapshotIndex: j,
        currentText: null,
        snapshotText: s.textContent,
      });
      j += 1;
    }
  }

  while (i < current.length) {
    const c = current[i];
    if (!c) break;
    aligned.push({
      key: `added-${i.toString()}`,
      status: "added",
      currentIndex: i,
      snapshotIndex: null,
      currentText: c.textContent,
      snapshotText: null,
    });
    i += 1;
  }

  while (j < snapshot.length) {
    const s = snapshot[j];
    if (!s) break;
    aligned.push({
      key: `removed-${j.toString()}`,
      status: "removed",
      currentIndex: null,
      snapshotIndex: j,
      currentText: null,
      snapshotText: s.textContent,
    });
    j += 1;
  }

  return collapseAdjacentReplace(aligned);
};

export const segmentRows = (rows: DiffRow[]): Segment[] => {
  const out: Segment[] = [];
  let unchangedRun = 0;
  let changeBuf: DiffRow[] = [];
  const flushUnchanged = (): void => {
    if (unchangedRun > 0) {
      out.push({ kind: "unchanged", count: unchangedRun });
      unchangedRun = 0;
    }
  };
  const flushChanges = (): void => {
    if (changeBuf.length > 0) {
      out.push({ kind: "changes", rows: changeBuf });
      changeBuf = [];
    }
  };
  for (const r of rows) {
    if (r.status === "unchanged") {
      flushChanges();
      unchangedRun += 1;
    } else {
      flushUnchanged();
      changeBuf.push(r);
    }
  }
  flushChanges();
  flushUnchanged();
  return out;
};

export const slideLabel = (row: DiffRow): string => {
  if (row.currentIndex !== null) return `Slide ${(row.currentIndex + 1).toString()}`;
  if (row.snapshotIndex !== null) {
    return `Slide ${(row.snapshotIndex + 1).toString()}`;
  }
  return "Slide";
};
